import { useSyncExternalStore } from 'react';
import { applyTheme } from './tokens';
import type { Theme } from './tokens';

/**
 * Which theme this browser is showing.
 *
 * Browser-local by design: there is no field that exists only on the web, so
 * a preference the phone has no opinion about must never become front matter.
 * Losing it on a new machine costs nothing.
 *
 * One store for the whole page, not a state per caller. The palette reaches
 * CSS through custom properties, but every tag tint is computed in JS from
 * the theme a component read — a caller holding its own copy keeps painting
 * the theme it mounted in.
 */

const KEY = 'pondering.theme';

let current: Theme | null = null;
const listeners = new Set<() => void>();

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

function systemQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: light)') : null;
}

function set(next: Theme): void {
  if (next === current) return;
  current = next;
  applyTheme(next);
  for (const listener of listeners) listener();
}

/** Follows the system only while no explicit choice has been made — once
 *  someone picks, the pick stands. */
function followSystem(): void {
  const media = systemQuery();
  media?.addEventListener('change', () => {
    if (stored() === null) set(media.matches ? 'light' : 'dark');
  });
}

export function currentTheme(): Theme {
  if (current === null) {
    current = stored() ?? (systemQuery()?.matches ? 'light' : 'dark');
    applyTheme(current);
    followSystem();
  }
  return current;
}

export function toggleTheme(): void {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Not remembered; the switch still holds for this page.
  }
  set(next);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, currentTheme, currentTheme);
  return { theme, toggle: toggleTheme };
}
